import React from 'react';
import FuelTank from './FuelTank';
import { useState } from 'react';
import Button from '@mui/material/Button';
import LocalGasStationOutlinedIcon from '@mui/icons-material/LocalGasStationOutlined';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import ModalCreateFuelTank from './ModalCreateFuelTank';
import ModalUpdateBatch from './ModalUpdateBatch';
import client from '../../../client';
import { DataGrid } from '@mui/x-data-grid';
import { useMessage } from 'app/shared-components/MessageUtils';
import { formatNumber, threeDecimalformatCurrency } from '../../../utils/numberFormatting';
import { useTranslation } from 'react-i18next';
import { IconButton, Box } from '@mui/material';

const Inventory = () => {
  const { t } = useTranslation('navigation');
  const [activeFuelBatches, setActiveFuelBatches] = React.useState([]);
  const [allFuelBatches, setAllFuelBatches] = React.useState([]);
  const [fuelTanks, setFuelTanks] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [openCreateTank, setOpenCreateTank] = useState(false);
  const [openUpdatePrice, setOpenUpdatePrice] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [fuelnLubeCatalog, setFuelnLubeCatalog] = useState([]);
  const [pageSize, setPageSize] = useState(5);
  const showMessage = useMessage();

  // Helper to format date as mm-dd-yyyy
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${mm}-${dd}-${yyyy}`;
  };

  // DataGrid columns definition
  const columns = [
    {
      field: 'date_created',
      headerName: t('DATE'),
      width: 120,
      valueFormatter: (params) => formatDate(params.value),
      flex: 1,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'batch_id',
      headerName: t('BATCH_ID'),
      width: 120,
      flex: 1,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
          <span>{params.value}</span>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedBatch(params.row);
              setOpenUpdatePrice(true);
            }}
            sx={{ 
              padding: '2px',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              }
            }}
          >
            <EditIcon fontSize="small" color="primary" />
          </IconButton>
        </Box>
      ),
    },
    {
      field: 'quantity',
      headerName: t('UNITS'),
      width: 100,
      type: 'number',
      valueFormatter: (params) => formatNumber(params.value),
      flex: 1,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'product_name',
      headerName: t('PRODUCT_NAME'),
      width: 100,
      type: 'string',
      flex: 1,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'price_per_unit',
      headerName: t('PRICE_PER_UNITS'),
      width: 150,
      valueFormatter: (params) => `$${threeDecimalformatCurrency(params.value)}`,
      flex: 1,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'tank_location',
      headerName: t('TANK'),
      width: 150,
      flex: 1,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'supplier',
      headerName: t('SUPPLIER'),
      width: 150,
      valueFormatter: (params) => params.value || '-',
      flex: 1,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'notes',
      headerName: t('NOTES'),
      width: 200,
      valueFormatter: (params) => params.value || '-',
      flex: 1,
      align: 'center',
      headerAlign: 'center',
    },
  ];

  // Transform data for DataGrid
  const dataGridRows = allFuelBatches.map((batch, idx) => {
    const tank = fuelTanks.find(t => t.id === batch.container_tank_id);
    return {
      id: batch.id || idx,
      ...batch,
      tank_location: tank ? tank.location : batch.container_tank_id,
    };
  });

  // Get compacctnum from localStorage
  let compacctnum = '';
  try {
    const lSData = JSON.parse(localStorage.getItem('persist:root'));
    const userData = JSON.parse(lSData.loadz_auth).AuthReducer.user;
    compacctnum = userData.company.compacctnum;
  } catch (e) {
    compacctnum = '';
  }

  const setTanks = async (data) => {
    setLoading(true);
    try {
      if (data && data.data) {
        setFuelTanks(data.data || []);
      }
    } catch (e) {
      setFuelTanks([]);
    }
    setLoading(false);
  };

  const getFullData = async () => {
      setLoading(true);
      try {
        const response = await client.get(`/fuel-inventory/fuel-inv-data/${compacctnum}`);
        if (response && response.data) {
          setFuelTanks(response.data.data.fuelTankData || []);
          setActiveFuelBatches(response.data.data.activeFuelBatchData || []);
          setAllFuelBatches(response.data.data.allFuelBatchData || []);
          setFuelnLubeCatalog(response.data.data.fuelNLubeCatalogData || []);          
        }
      } catch (e) {
        setFuelTanks([]);
      }
      setLoading(false);
  };

  React.useEffect(() => {
      getFullData();
    }, []);

  const tankCapacity = 2000;
  const batchHeights = activeFuelBatches.map(batch => ({
    ...batch,
    percent: batch.gallons / tankCapacity,
  }));
  const totalGallons = activeFuelBatches.reduce((sum, batch) => sum + batch.gallons, 0);

  const datagridHeaderStyle = `
      .MuiDataGrid-columnsContainer {
        background-color: #edca33 !important;
      }
    `;

  return (
    <div className="p-24">
      <style>{datagridHeaderStyle}</style>
      <ModalCreateFuelTank isOpen={openCreateTank} setIsOpen={setOpenCreateTank} onCreated={getFullData} _fuelnLubeCatalog={fuelnLubeCatalog} />
      <ModalUpdateBatch
        isOpen={openUpdatePrice} 
        setIsOpen={setOpenUpdatePrice} 
        selectedBatch={selectedBatch}
        onUpdated={getFullData}
      />
      <div className='flex flex-1 justify-between items-center mb-4'>
        <h1 className="text-2xl font-bold">{t('INVENTORY')}</h1>
        {/* Button to create tank */}
        <div>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => setOpenCreateTank(true)}
          >
            {t('ADD_TANK')}
          </Button>
        </div>
      </div>
      {/* Tank chart extracted to FuelTank component */}
      {loading ? (
        <div className="flex flex-col items-center justify-center min-w-[220px]">
          <b style={{ fontSize: '2rem', fontStyle: 'italic', color: '#888' }}>{t('LOADING')}</b>
        </div>
      ) : fuelTanks.length > 0 ? (
        <>
          <div style={{ width: '100% !important' }} className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-6 lg:grid-cols-6 xl:grid-cols-6 gap-4 sm:gap-6 md:gap-8">
            {fuelTanks
              .sort((a, b) => {
                // Sort tanks: first those that have content (batches)
                const aHasContent = getBatches(a.id).length > 0;
                const bHasContent = getBatches(b.id).length > 0;
                
                if (aHasContent && !bHasContent) return -1; // a goes first
                if (!aHasContent && bHasContent) return 1;  // b goes first
                return 0; // maintain original order if both have or don't have content
              })
              .map((tank, idx) => (            
              <FuelTank
                key={tank.id || idx}
                tankCapacity={tank.capacity || tank.tankCapacity || 2000}
                tankKindOfContent={tank.product_name || ''}
                tankLocation={tank.location || ''}
                tankIdentifier={tank.id || ''}
                tankNotes={tank.notes || ''}
                batchHeights={getBatches(tank.id) || []}
                onAddPurchase={true}
                onAdded={getFullData}
                inventoryValue={tank.inventory_value || 0}
                tankData={tank}
                compacctnum={compacctnum}
                showAdjustmentButton={true}
              />
            ))}
          </div>

          <div className="mt-8 w-full">
            <h2 style={{ marginTop: '3rem', marginBottom: '0.5rem' }} className="text-xl font-semibold mb-2">{t('PURCHASES_HISTORY')}</h2>
            <div style={{ height: '100%', width: '100%', overflowX: 'auto' }} className="custom-datagrid-header">
              <DataGrid
                rows={dataGridRows}
                columns={columns}
                pageSize={pageSize}
                onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                rowsPerPageOptions={[5, 10, 20]}
                pagination
                disableSelectionOnClick
                autoHeight
                sx={{
                  '& .MuiDataGrid-columnHeaders': {
                    backgroundColor: '#edca33',
                    fontWeight: 'bold',
                    '& .MuiDataGrid-columnHeaderTitle': {
                      textAlign: 'center',
                      width: '100%',
                    },
                  },
                  '& .MuiDataGrid-cell': {
                    borderRight: '1px solid #e0e0e0',
                    textAlign: 'center !important',
                    display: 'flex !important',
                    alignItems: 'center !important',
                    justifyContent: 'center !important',
                  },
                  '& .MuiDataGrid-cellContent': {
                    textAlign: 'center !important',
                    width: '100%',
                  },
                  '& .MuiDataGrid-row:hover': {
                    backgroundColor: '#f5f5f5',
                  },
                }}
                noRowsOverlay={() => (
                  <div className="flex items-center justify-center h-full text-gray-500">
                    {t('NO_PURCHASES_FOUND')}
                  </div>
                )}
              />
            </div>
          </div>
        </>
      ) : (
        <div className="flex flex-col items-center justify-center min-w-[220px]">
          <b style={{ fontSize: '3.2rem', fontStyle: 'italic', color: '#e58741' }}>{t('NO_TANKS_REGISTERED_INVENTORY')}</b>
        </div>
      )}
    </div>
  );

  function getBatches(tankId) {
    let batches = activeFuelBatches.filter(batch => batch.container_tank_id === tankId);
    return batches.sort((a, b) => a.batch_id.split('-')[2] - b.batch_id.split('-')[2]);
  }
};

export default Inventory;
